import { useMemo, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";

import type { PlanReviewOrdinanceCompliance, PlanReviewRun } from "@/api/plan_review";
import { MunicipalityDocumentPickerPanel } from "@/components/chat/DocumentSelector";
import { PlanReviewOrdinanceProgressPanel } from "@/components/plan_review/PlanReviewOrdinanceProgressPanel";
import { humanizeOrdinanceOutcome } from "@/components/plan_review/planReviewDisplay";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MAX_CODE_DOCUMENTS = 10;

type PlanReviewZoningSectionProps = {
  reviewRun: PlanReviewRun | null;
  reviewComplete: boolean;
  runInFlight: boolean;
  municipalityId: number | null | undefined;
  ordinanceCompliance: PlanReviewOrdinanceCompliance | undefined;
  complianceLoading: boolean;
  annotatedPlanUrl: string | null | undefined;
  sourcePlanUrl: string | null | undefined;
  onRunCompliance: (params: { documentIds: number[]; desiredZoning: string }) => void;
};

type OutcomeKey = "compliant" | "non_compliant" | "needs_review";

const OUTCOME_LABELS: Record<OutcomeKey, string> = {
  compliant: "Meets code",
  non_compliant: "Does not meet code",
  needs_review: "Planner should verify",
};

export function PlanReviewZoningSection({
  reviewRun,
  reviewComplete,
  runInFlight,
  municipalityId,
  ordinanceCompliance,
  complianceLoading,
  annotatedPlanUrl,
  sourcePlanUrl,
  onRunCompliance,
}: PlanReviewZoningSectionProps) {
  const [zoningDistrict, setZoningDistrict] = useState(reviewRun?.desired_zoning ?? "");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>(
    () => ordinanceCompliance?.source_document_ids ?? [],
  );
  const [viewAllOpen, setViewAllOpen] = useState(false);

  const complianceComplete = ordinanceCompliance?.status === "complete";
  const complianceInFlight = ["pending", "processing"].includes(ordinanceCompliance?.status ?? "");
  const canRun =
    reviewComplete &&
    !complianceInFlight &&
    !complianceLoading &&
    !!municipalityId &&
    selectedDocumentIds.length >= 1 &&
    selectedDocumentIds.length <= MAX_CODE_DOCUMENTS &&
    zoningDistrict.trim().length > 0;

  const missingForRun = useMemo(() => {
    const missing: string[] = [];
    if (!zoningDistrict.trim()) missing.push("Zoning district");
    if (selectedDocumentIds.length === 0) missing.push("At least one code document");
    if (selectedDocumentIds.length > MAX_CODE_DOCUMENTS) {
      missing.push(`At most ${MAX_CODE_DOCUMENTS} code documents`);
    }
    return missing;
  }, [selectedDocumentIds.length, zoningDistrict]);

  const itemsByOutcome = useMemo(() => {
    const items = ordinanceCompliance?.items ?? [];
    const grouped: Record<OutcomeKey, typeof items> = {
      compliant: [],
      non_compliant: [],
      needs_review: [],
    };
    for (const item of items) {
      const key = (item.outcome ?? "needs_review") as OutcomeKey;
      if (grouped[key]) grouped[key].push(item);
      else grouped.needs_review.push(item);
    }
    return grouped;
  }, [ordinanceCompliance?.items]);

  if (!reviewRun?.id) {
    return (
      <p className="text-sm text-muted-foreground">
        Start or open a drawing review first, then check results against your zoning code here.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-foreground">Check against zoning code</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Compare plan measurements to the code documents you select. Always verify before issuing decisions.
        </p>
      </div>

      {(annotatedPlanUrl || sourcePlanUrl) && (
        <div className="flex flex-wrap gap-2">
          {annotatedPlanUrl ? (
            <Button variant="default" asChild>
              <a href={annotatedPlanUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Open annotated PDF
              </a>
            </Button>
          ) : null}
          {sourcePlanUrl ? (
            <Button variant="outline" asChild>
              <a href={sourcePlanUrl} target="_blank" rel="noopener noreferrer">
                Open original plan
              </a>
            </Button>
          ) : null}
        </div>
      )}

      {!reviewComplete && (
        <Alert>
          <AlertDescription>
            {runInFlight || reviewRun.status === "pending"
              ? "Finish the drawing review before running a zoning code check."
              : "The drawing review did not complete. Fix or restart the review before checking against code."}
          </AlertDescription>
        </Alert>
      )}

      {reviewComplete && !complianceComplete && !complianceInFlight && (
        <div className="space-y-4 rounded-lg border border-border bg-card p-4">
          <div className="space-y-2">
            <Label htmlFor="plan-review-zoning-district">Zoning district</Label>
            <Input
              id="plan-review-zoning-district"
              value={zoningDistrict}
              onChange={(event) => setZoningDistrict(event.target.value)}
              placeholder="e.g. R-3, C-2"
            />
          </div>

          {municipalityId ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Which code documents?</Label>
                <span className="text-xs text-muted-foreground">
                  {selectedDocumentIds.length} of {MAX_CODE_DOCUMENTS} selected
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Select the ordinances or codebooks to check this plan against (up to {MAX_CODE_DOCUMENTS}).
              </p>
              <div className="max-h-[40vh] overflow-hidden rounded-lg border">
                <div
                  className="ordinal-scrollbar document-selector-dialog-scroll max-h-[40vh] min-w-0 overflow-auto overflow-x-hidden p-3"
                  data-testid="plan-review-code-documents-scroll"
                >
                  <MunicipalityDocumentPickerPanel
                    municipalityId={municipalityId}
                    selectedDocumentIds={selectedDocumentIds}
                    onSelectionChange={setSelectedDocumentIds}
                    maxSelection={MAX_CODE_DOCUMENTS}
                  />
                </div>
              </div>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertDescription>Choose your municipality in the header to select code documents.</AlertDescription>
            </Alert>
          )}

          {missingForRun.length > 0 && (
            <p className="text-sm text-muted-foreground">
              To continue, add: {missingForRun.join(", ")}
            </p>
          )}

          <Button disabled={!canRun} onClick={() => onRunCompliance({ documentIds: selectedDocumentIds, desiredZoning: zoningDistrict.trim() })}>
            {complianceLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Check against zoning code
          </Button>
        </div>
      )}

      {ordinanceCompliance?.status === "failed" && (
        <Alert variant="destructive">
          <AlertDescription>
            {ordinanceCompliance.error_message || "Zoning code check couldn't finish. Try again or contact support."}
          </AlertDescription>
        </Alert>
      )}

      {complianceInFlight && ordinanceCompliance ? (
        <PlanReviewOrdinanceProgressPanel compliance={ordinanceCompliance} />
      ) : null}

      {complianceComplete && ordinanceCompliance?.source_document_names?.length ? (
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Checked against:</span>
          <ul className="mt-1 list-disc pl-5">
            {ordinanceCompliance.source_document_names.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {complianceComplete && ordinanceCompliance?.summary ? (
        <>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-end justify-between gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor="plan-review-zoning-district">Zoning district</Label>
                <Input
                  id="plan-review-zoning-district"
                  value={zoningDistrict}
                  onChange={(event) => setZoningDistrict(event.target.value)}
                  placeholder="e.g. R-3, C-2"
                />
              </div>
              <Button
                variant="outline"
                disabled={
                  complianceLoading ||
                  complianceInFlight ||
                  selectedDocumentIds.length < 1 ||
                  zoningDistrict.trim().length === 0
                }
                onClick={() =>
                  onRunCompliance({
                    documentIds: selectedDocumentIds,
                    desiredZoning: zoningDistrict.trim(),
                  })
                }
              >
                Run zoning check again
              </Button>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {(Object.keys(OUTCOME_LABELS) as OutcomeKey[]).map((key) => {
              const preview = itemsByOutcome[key].slice(0, 3);
              const count =
                key === "compliant"
                  ? ordinanceCompliance.summary?.compliant ?? 0
                  : key === "non_compliant"
                    ? ordinanceCompliance.summary?.non_compliant ?? 0
                    : ordinanceCompliance.summary?.needs_review ?? 0;
              return (
                <Card key={key} className="p-4">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {OUTCOME_LABELS[key]}
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-foreground">{count}</div>
                  {preview.length > 0 ? (
                    <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                      {preview.map((item) => (
                        <li key={item.id} className="line-clamp-2">
                          {item.topic}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </Card>
              );
            })}
          </div>
          {(ordinanceCompliance.items?.length ?? 0) > 0 && (
            <Button variant="outline" onClick={() => setViewAllOpen((open) => !open)}>
              {viewAllOpen ? "Hide full list" : "View all"}
            </Button>
          )}
          {viewAllOpen && (
            <div className="max-h-[50vh] overflow-hidden rounded-lg border">
              <div
                className="ordinal-scrollbar max-h-[50vh] min-w-0 space-y-4 overflow-auto overflow-x-hidden p-4"
                data-testid="plan-review-zoning-results-scroll"
              >
                {(Object.keys(OUTCOME_LABELS) as OutcomeKey[]).map((key) => {
                  const sectionItems = itemsByOutcome[key];
                  if (sectionItems.length === 0) return null;
                  return (
                    <div key={key}>
                      <h3 className="text-sm font-semibold text-foreground">{OUTCOME_LABELS[key]}</h3>
                      <ul className="mt-2 space-y-3">
                        {sectionItems.map((item) => (
                          <li key={item.id} className="rounded-md border border-border p-3 text-sm">
                            <div className="font-medium text-foreground">{item.topic}</div>
                            {item.explanation ? (
                              <p className="mt-1 text-muted-foreground">{item.explanation}</p>
                            ) : null}
                            <div className="mt-2 text-xs text-muted-foreground">
                              {humanizeOrdinanceOutcome(item.outcome)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
